// Web push on this device (P-004 milestone 3, docs/alerts.md): subscribe the browser through the
// app's service worker (web/pwa.ts builds sw.js, which shows the notification and opens its page
// on a click), with the server's VAPID public key. The board stores the subscription; nothing here
// sends anything.

export type PushSupport = 'ok' | 'unsupported' | 'no-worker' | 'denied';

/** The subscription's JSON, as the API takes it (PushSubscription.toJSON()). */
export interface PushJson {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

function supported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof window !== 'undefined' && 'PushManager' in window && 'Notification' in window;
}

/** The app's service worker registration, if it runs (it does in the built app, not under Vite). */
async function registration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!supported()) return undefined;
  return navigator.serviceWorker.getRegistration('/');
}

/** Whether this browser can subscribe at all, before asking. */
export async function pushSupport(): Promise<PushSupport> {
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return (await registration()) ? 'ok' : 'no-worker';
}

/** This device's current subscription's endpoint, if it has one. */
export async function currentEndpoint(): Promise<string | null> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export class PushRefused extends Error {}

/** Ask for permission and subscribe this device. Throws PushRefused with a reason to show. */
export async function subscribe(publicKey: string): Promise<PushJson> {
  if (!supported()) throw new PushRefused('This browser cannot receive push notifications.');
  const reg = await registration();
  if (!reg) throw new PushRefused('The app’s service worker is not running here; open the installed app or the live site.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new PushRefused('Notifications are blocked for this site; allow them in the browser’s settings.');
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) }));
  const json = sub.toJSON() as Partial<PushJson>;
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new PushRefused('The browser gave an incomplete subscription.');
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/** Drop this device's subscription in the browser (the caller removes it on the server). */
export async function unsubscribe(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  await sub?.unsubscribe();
}
